/**
 * Lesson endpoints — the lesson itself, its artifacts, settings, quiz
 * attempts and progress.
 */
import type {
  StepState,
  StepView,
  SourceView,
  SourcesView,
  PipelineStatusResponse,
  StepActionResponse,
  RetryFailedResponse,
} from '@/types/pipeline';
import type {
  AddExtrasAttachmentRequest,
  BuildPlanResponse,
  BuildPreviewResponse,
  ClassifiedChatResponse,
  ConversationHistoryMessage,
  ConversationSummary,
  DiscoverTopicsResponse,
  DoubtSessionRecording,
  DoubtSessionSummary,
  GenerateLessonRequest,
  GenerateLessonResponse,
  InsightsResponse,
  LessonConcepts,
  LessonContext,
  LessonExtras,
  LessonFlashcards,
  LessonGlossary,
  LessonHighlightsResponse,
  LessonMetadata,
  LessonMindmap,
  LessonPodcast,
  SectionPodcastsResponse,
  GenerateSectionPodcastResponse,
  LessonProgressResponse,
  LessonQuiz,
  LessonText,
  LessonVideo,
  LinkPreviewResponse,
  LogSourceViewResponse,
  MyLearningResponse,
  MyLearningTagOptionsResponse,
  MyProgressResponse,
  OnDemandContentType,
  PipelineStep,
  ProgressPeriod,
  RetryResponse,
  SourceAssessmentResponse,
  SubmitQuizAttemptPayload,
  SubmitQuizAttemptResponse,
  UserLessonItem,
  UserLessonSettings,
} from '@/types/api';
import { apiClient } from './client';

/**
 * Lesson API endpoints
 */
export const lessonApi = {
  /**
   * Assess source size for scoping
   */
  assessSources: (sourceIds: string[]) =>
    apiClient.post<SourceAssessmentResponse>('/lessons/assess-sources', {
      source_ids: sourceIds,
    }),

  /**
   * Discover topics from large sources
   */
  discoverTopics: (sourceIds: string[]) =>
    apiClient.post<DiscoverTopicsResponse>('/lessons/discover-topics', {
      source_ids: sourceIds,
    }),

  /**
   * Generate new lesson from topic.
   *
   * Backend `POST /lessons/generate` is multipart-only since Phase 1 of
   * the byte-driven pipeline. We always send FormData; if the request has
   * no `files`, the multipart payload still works (backend treats the
   * absent file list as the synthetic-source path).
   */
  generateLesson: (request: GenerateLessonRequest) => {
    const formData = new FormData();
    formData.append('topic', request.topic);
    request.source_ids?.forEach(id => formData.append('source_ids', id));
    if (request.focus_topic) {
      formData.append('focus_topic', request.focus_topic);
    }
    if (request.folder_id) {
      formData.append('folder_id', request.folder_id);
    }
    if (request.settings) {
      formData.append('settings', JSON.stringify(request.settings));
    } else if (request.depth) {
      // Legacy fallback - only sent when no settings was provided. Backend
      // maps depth -> settings.flow on the way in.
      formData.append('depth', request.depth);
    }
    request.files?.forEach(file => formData.append('files', file));
    if (request.links && request.links.length > 0) {
      formData.append('links', JSON.stringify(request.links));
    }
    if (request.texts && request.texts.length > 0) {
      formData.append('texts', JSON.stringify(request.texts));
    }
    // Deep-dive promotion linkage - both fields required together.
    // Backend marks the source chat message + auto-attaches the new
    // lesson to the source lesson's Extras when these are present.
    if (request.from_deep_dive_lesson && request.from_deep_dive_message) {
      formData.append('from_deep_dive_lesson', request.from_deep_dive_lesson);
      formData.append('from_deep_dive_message', request.from_deep_dive_message);
    }
    return apiClient.postMultipart<GenerateLessonResponse>(
      '/lessons/generate',
      formData
    );
  },

  /**
   * Get lesson metadata (polled for overview status)
   */
  getLesson: (lessonId: string) =>
    apiClient.get<LessonMetadata>(`/lessons/${lessonId}`),

  /**
   * Get lesson context (polled)
   */
  getLessonContext: (lessonId: string) =>
    apiClient.get<LessonContext>(`/lessons/${lessonId}/context`),

  /**
   * Get lesson text (polled)
   */
  getLessonText: (lessonId: string) =>
    apiClient.get<LessonText>(`/lessons/${lessonId}/text`),

  /**
   * Get lesson video (polled)
   */
  getLessonVideo: (lessonId: string) =>
    apiClient.get<LessonVideo>(`/lessons/${lessonId}/video`),

  /**
   * List recorded doubt sessions for a lesson (newest first).
   */
  listDoubtSessions: (lessonId: string) =>
    apiClient.get<{ sessions: DoubtSessionSummary[] }>(
      `/lessons/${lessonId}/doubt-sessions`
    ),

  /**
   * Get the ordered (user, AI) audio + scene segments for one session,
   * for continuous-scrub replay.
   */
  getDoubtSessionRecording: (lessonId: string, sessionId: string) =>
    apiClient.get<DoubtSessionRecording>(
      `/lessons/${lessonId}/doubt-sessions/${sessionId}/recording`
    ),

  /**
   * Hard-delete a recorded doubt session (DB row + all S3 artifacts +
   * associated chat messages).
   */
  deleteDoubtSession: (lessonId: string, sessionId: string) =>
    apiClient.delete<{ status: string; session_id: string }>(
      `/lessons/${lessonId}/doubt-sessions/${sessionId}`
    ),

  /**
   * Get lesson mindmap (polled)
   */
  getLessonMindmap: (lessonId: string) =>
    apiClient.get<LessonMindmap>(`/lessons/${lessonId}/mindmap`),

  /**
   * What the lesson's read sources cover - names, summaries and up to 8
   * sections each - for the build view while the outline is being made.
   * Read-only and cheap; fetched once, not polled.
   */
  getBuildPreview: (lessonId: string) =>
    apiClient.get<BuildPreviewResponse>(`/lessons/${lessonId}/build-preview`),

  /** The draft outline while its concepts are written (build view). */
  getBuildPlan: (lessonId: string) =>
    apiClient.get<BuildPlanResponse>(`/lessons/${lessonId}/build-plan`),

  // The legacy per-artifact retry endpoints (/retry/overview, /retry/text,
  // /retry/mindmap, /retry/video, /retry/{content_type}) are GONE. Use
  // `retryStep(lessonId, step)` below, which drives the backend's
  // STEP_REGISTRY.
  //
  // They were not just redundant, they behaved DIFFERENTLY: the registry route
  // writes `pipeline_status.{step} = QUEUED`, while the legacy route set only
  // `<artifact>.status = PENDING` and never touched `pipeline_status` — so a
  // legacy retry left the pipeline-status endpoint reporting the old state.
  // That is the skeleton-render bug `on_demand.py` worked around with a raw
  // Mongo write.

  // ===== On-Demand Content =====

  /**
   * Trigger on-demand content generation (registry-unified generate endpoint).
   */
  generateContent: (lessonId: string, contentType: OnDemandContentType) =>
    apiClient.post<StepActionResponse>(
      `/lessons/${lessonId}/steps/${contentType}/generate`,
      {}
    ),

  /**
   * Get quiz content
   */
  getQuiz: (lessonId: string) =>
    apiClient.get<LessonQuiz>(`/lessons/${lessonId}/quiz`),

  /**
   * Get flashcards content
   */
  getFlashcards: (lessonId: string) =>
    apiClient.get<LessonFlashcards>(`/lessons/${lessonId}/flashcards`),

  /**
   * Get glossary content
   */
  getGlossary: (lessonId: string) =>
    apiClient.get<LessonGlossary>(`/lessons/${lessonId}/glossary`),

  /**
   * The lesson's sections, each with its podcast's state. Podcasts are made
   * per section, on demand.
   */
  getSectionPodcasts: (lessonId: string) =>
    apiClient.get<SectionPodcastsResponse>(`/lessons/${lessonId}/podcasts`),

  /**
   * One section's podcast (audio URL + sentence-timed transcript)
   */
  getSectionPodcast: (lessonId: string, sectionIndex: number) =>
    apiClient.get<LessonPodcast>(
      `/lessons/${lessonId}/podcasts/${sectionIndex}`
    ),

  /**
   * Make one section's podcast: one unit of the podcast quota (402 at the
   * cap, or `upgrade_required` for a later section on Free).
   */
  generateSectionPodcast: (lessonId: string, sectionIndex: number) =>
    apiClient.post<GenerateSectionPodcastResponse>(
      `/lessons/${lessonId}/podcasts/${sectionIndex}/generate`
    ),

  /**
   * Get extras (manually-curated attachments) for a lesson.
   */
  getExtras: (lessonId: string) =>
    apiClient.get<LessonExtras>(`/lessons/${lessonId}/extras`),

  /**
   * Add a lesson reference or external link to the lesson's Extras tab.
   */
  addExtrasAttachment: (lessonId: string, body: AddExtrasAttachmentRequest) =>
    apiClient.post<LessonExtras>(
      `/lessons/${lessonId}/extras/attachments`,
      body
    ),

  /**
   * Remove an attachment from the lesson's Extras tab.
   */
  deleteExtrasAttachment: (lessonId: string, attachmentId: string) =>
    apiClient.delete<LessonExtras>(
      `/lessons/${lessonId}/extras/attachments/${attachmentId}`
    ),

  /**
   * Probe a URL for `<title>` + `<meta description>` so the "Add link"
   * UI can pre-fill fields. Returns `{title: null, description: null}`
   * on any fetch failure - never throws.
   */
  previewExtrasLink: (url: string) =>
    apiClient.post<LinkPreviewResponse>(`/lessons/extras/preview-link`, {
      url,
    }),

  /**
   * List chat conversations for a lesson, most-recent first. Powers the
   * chat panel's history dropdown.
   *
   * Scoped to the lesson's current version by default - set
   * `includeAllVersions` to surface conversations from previous versions
   * too (e.g. an "older chats" toggle).
   */
  listConversations: (
    lessonId: string,
    opts: { includeAllVersions?: boolean } = {}
  ) => {
    const qs = opts.includeAllVersions ? '?include_all_versions=true' : '';
    return apiClient.get<{ conversations: ConversationSummary[] }>(
      `/lessons/${lessonId}/conversations${qs}`
    );
  },

  /**
   * Create a new (empty) conversation in a lesson.
   * Returns the new conversation's summary.
   */
  createConversation: (lessonId: string) =>
    apiClient.post<ConversationSummary>(`/lessons/${lessonId}/conversations`),

  /**
   * Load a single conversation's full message history.
   */
  getConversation: (lessonId: string, conversationId: string) =>
    apiClient.get<{
      conversation_id: string;
      title: string | null;
      messages: ConversationHistoryMessage[];
    }>(`/lessons/${lessonId}/conversations/${conversationId}`),

  /**
   * Delete a single conversation. Other threads in the lesson are untouched.
   */
  deleteConversation: (lessonId: string, conversationId: string) =>
    apiClient.delete<void>(
      `/lessons/${lessonId}/conversations/${conversationId}`
    ),

  /**
   * Get the lesson-wide aggregate of classified chat exchanges
   * (doubts + deep-dives across every conversation). Powers both the
   * Appendix's Doubts tab and the Extras' Suggested Deep-Dives section.
   *
   * Scoped to the current lesson version by default; pass
   * `includeAllVersions` to merge in older versions' entries.
   */
  getClassifiedChat: (
    lessonId: string,
    opts: { includeAllVersions?: boolean } = {}
  ) => {
    const qs = opts.includeAllVersions ? '?include_all_versions=true' : '';
    return apiClient.get<ClassifiedChatResponse>(
      `/lessons/${lessonId}/chat/classified${qs}`
    );
  },

  /**
   * Get concepts content
   */
  getConcepts: (lessonId: string) =>
    apiClient.get<LessonConcepts>(`/lessons/${lessonId}/concepts`),

  /**
   * Retry on-demand content generation (registry-unified retry endpoint).
   */
  retryContent: (lessonId: string, contentType: OnDemandContentType) =>
    apiClient.post<StepActionResponse>(
      `/lessons/${lessonId}/steps/${contentType}/retry`,
      {}
    ),

  /**
   * Delete lesson
   */
  deleteLesson: (lessonId: string) => apiClient.delete(`/lessons/${lessonId}`),

  /**
   * Get user's lessons (My Learning dashboard)
   */
  getUserLessons: (params?: {
    search?: string;
    page?: number;
    limit?: number;
    /** Folder UUID to scope to. Pass 'root' for unfilred / top-level lessons. */
    folderId?: string | 'root';
    /** When true, the response's `items` field carries a unified
     *  folder + lesson stream sorted by updated_at desc; pagination
     *  spans both types so deleting a folder shifts the stream. */
    includeFolders?: boolean;
    /** Type filter applied to the unified stream BEFORE pagination.
     *  Defaults to 'all'. Ignored unless `includeFolders` is true. */
    itemType?: 'all' | 'folders' | 'lessons';
    /** Tag filter (OR match) applied to both lessons and folders. */
    tags?: string[];
  }) => {
    const query = new URLSearchParams();
    if (params?.search) query.append('search', params.search);
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.folderId) query.append('folder_id', params.folderId);
    if (params?.includeFolders) query.append('include_folders', 'true');
    if (params?.itemType && params.itemType !== 'all') {
      query.append('item_type', params.itemType);
    }
    if (params?.tags && params.tags.length > 0) {
      for (const tag of params.tags) {
        query.append('tags', tag);
      }
    }

    const queryString = query.toString();
    const endpoint = queryString ? `/lessons?${queryString}` : '/lessons';

    return apiClient.get<MyLearningResponse>(endpoint);
  },

  /**
   * List the distinct tags the user has applied to lessons + folders,
   * with combined counts. Powers the My Learning tag filter picker.
   */
  getMyLearningTags: () =>
    apiClient.get<MyLearningTagOptionsResponse>('/lessons/tags'),

  /**
   * Update lesson metadata (title, description, tags, folder assignment).
   *
   * Pure metadata edit - does NOT bump the version or regenerate any
   * artifact. Use `editPrompt` for prompt changes that should regenerate.
   */
  updateLesson: (
    lessonId: string,
    data: {
      title?: string;
      description?: string;
      tags?: string[];
      folder_id?: string | null;
      /**
       * When set, title/description/tags edits land on this specific
       * LessonVersion's snapshot. Mirrored back onto the lesson row only
       * if the version equals current_version. Omit for legacy "edit the
       * row directly" behavior.
       */
      version?: string;
    }
  ) => apiClient.patch<UserLessonItem>(`/lessons/${lessonId}`, data),

  // --------------------------------------------------------------------------
  // Phase B endpoints - pipeline-status polling, retry, prompt edit, title,
  // and the per-concept highlights artifact.
  // --------------------------------------------------------------------------

  /**
   * Aggregated pipeline status - single poll endpoint that drives the
   * skeleton-first lesson page. Returns per-step state for every artifact
   * the lesson cares about (overview, byte_selection, text, mindmap, ...).
   */
  getPipelineStatus: (lessonId: string) =>
    apiClient.get<PipelineStatusResponse>(
      `/lessons/${lessonId}/pipeline-status`
    ),

  /**
   * Retry a single pipeline step (any phase). Backend resets that step's
   * StepRecord to 'queued', clears its error, and re-queues the task - works
   * even when the step is stuck in queued/processing (dead-worker rescue).
   * Does NOT bump the lesson version.
   */
  retryStep: (lessonId: string, stepName: PipelineStep) =>
    apiClient.post<StepActionResponse>(
      `/lessons/${lessonId}/steps/${stepName}/retry`,
      {}
    ),

  /**
   * Manually generate an on-demand / off-flow step. Requires context ready.
   */
  generateStep: (lessonId: string, stepName: PipelineStep) =>
    apiClient.post<StepActionResponse>(
      `/lessons/${lessonId}/steps/${stepName}/generate`,
      {}
    ),

  /** Retry every currently-failed step in one call (the "Retry all" action). */
  retryAllFailed: (lessonId: string) =>
    apiClient.post<RetryFailedResponse>(
      `/lessons/${lessonId}/retry-failed`,
      {}
    ),

  /** Re-ingest a single failed source and re-open the context gate. */
  retrySource: (lessonId: string, sourceId: string) =>
    apiClient.post<StepActionResponse>(
      `/lessons/${lessonId}/sources/${sourceId}/retry`,
      {}
    ),

  /**
   * Recover a lesson whose sources ALL failed (ALL_SOURCES_FAILED). Clears the
   * lesson-level error, re-opens the context latch, and re-ingests sources
   * (re-dispatches failed user sources, or regenerates synthetic ones). Backs
   * the whole-page source-failure block's primary action.
   */
  retrySources: (lessonId: string) =>
    apiClient.post<StepActionResponse>(
      `/lessons/${lessonId}/retry-sources`,
      {}
    ),

  /**
   * Save a draft prompt edit. Stores the prompt on `staged_prompt` without
   * creating a version - the user applies it later via the "Create new
   * version" button. If the new prompt equals the current `user_prompt`,
   * the server clears the draft (`staged_prompt = null`).
   */
  editPrompt: (lessonId: string, prompt: string) =>
    apiClient.post<{ lesson_id: string; staged_prompt: string | null }>(
      `/lessons/${lessonId}/edit-prompt`,
      { prompt }
    ),

  /**
   * Get per-concept highlights (carousel cards). Status="pending" when the
   * artifact hasn't been generated yet - the UI shows a skeleton in that
   * case rather than 404'ing the panel.
   */
  getHighlights: (lessonId: string) =>
    apiClient.get<LessonHighlightsResponse>(`/lessons/${lessonId}/highlights`),
};

// Canonical pipeline types are imported at the top of this file; re-export so
// existing imports from '@/lib/api-client' keep working.
export type {
  StepState,
  StepView,
  SourceView,
  SourcesView,
  PipelineStatusResponse,
  StepActionResponse,
  RetryFailedResponse,
};

// ---------------------------------------------------------------------------
// User lesson-settings API (per-account defaults + saved custom flows).
// Lives on the users router, not lessons - same path layout as user prefs.
// ---------------------------------------------------------------------------

export const userLessonSettingsApi = {
  get: () => apiClient.get<UserLessonSettings>('/users/me/lesson-settings'),

  update: (payload: Partial<UserLessonSettings>) =>
    apiClient.put<UserLessonSettings>('/users/me/lesson-settings', payload),
};

export const quizAttemptApi = {
  /** Submit a quiz attempt. Idempotent on `attempt_id` - re-submission returns the prior row. */
  submit: (lessonId: string, payload: SubmitQuizAttemptPayload) =>
    apiClient.post<SubmitQuizAttemptResponse>(
      `/lessons/${lessonId}/quiz-attempts`,
      payload
    ),
};

// ============================================================================
// PROGRESS INSIGHTS - LLM-backed narrative cards
// ============================================================================

export const progressApi = {
  /** Per-lesson Coverage, 3-state status, engagement strip. */
  getLessonProgress: (lessonId: string) =>
    apiClient.get<LessonProgressResponse>(`/lessons/${lessonId}/progress`),

  /** Log a source link click. Engagement counts distinct source_id. */
  logSourceView: (lessonId: string, sourceId: string) =>
    apiClient.post<LogSourceViewResponse>(
      `/lessons/${lessonId}/sources/${sourceId}/viewed`
    ),

  /** Calendar-bounded user-level progress aggregates. */
  getMyProgress: (period: ProgressPeriod = 'this_week', year?: number) => {
    const params = new URLSearchParams({ period });
    if (year !== undefined) params.set('year', String(year));
    return apiClient.get<MyProgressResponse>(
      `/analytics/me/progress?${params.toString()}`
    );
  },

  /** LLM-backed insight cards. Cached per (user, period, UTC day). */
  getInsights: (period: ProgressPeriod = 'this_week', forceRefresh = false) => {
    const params = new URLSearchParams({ period });
    if (forceRefresh) params.set('force_refresh', 'true');
    return apiClient.get<InsightsResponse>(
      `/analytics/me/insights?${params.toString()}`
    );
  },
};

// ============================================================================
// BUG REPORTS - user-submitted feedback from Settings
// ============================================================================
